import React, { useEffect, useState } from 'react';
import axios from 'axios';
import '../styles/HelpTaxonomy.css';
import '../styles/NewDashboard.css';

const pages = {
  'Market Brief': [
    [
      'Activity',
      'Number of recorded conversations and contributing field team members in the selected period.',
    ],
    [
      'Items to review',
      'Locations, crops or brands with enough conversation evidence to warrant a closer look. Start with the largest change, then compare the underlying comments. This is not a sales forecast.',
    ],
  ],
  'Field Intelligence': [
    [
      'Issue mentions',
      'Count of concern, objection, urgency or damage signals linked to a crop mention. Multiple signals can arise in one conversation; it is not a count of affected farms. Compare with conversations and contributors before deciding where to investigate. Weather and supply issues are not yet classified.',
    ],
    [
      'Province map',
      'Color is issue mentions per 100 crop conversations in that province. The denominator is recorded crop conversations, not farms or hectares. Grey means no matching conversations. A dark province with very few recordings should be checked first, not treated as a confirmed outbreak.',
    ],
    [
      'Contributors',
      'Distinct field team members contributing conversations to the selected location and crop.',
    ],
    [
      'Movement',
      'Change in conversation mentions across the selected weeks. Select a location and crop, then check the line chart against total recording activity. Increased mentions can reflect more recordings, not an increased field incidence.',
    ],
    ['Conversations', 'The full transcript register for the selected global date and geography filters.'],
  ],
  'Brand Intelligence': [
    [
      'Brand sentiment',
      'Conversation-level positive, negative, mixed and neutral assessments associated with the named brand. Read the comments: neutral may mean use was mentioned without an opinion.',
    ],
    [
      'Net sentiment',
      '(Positive assessments minus negative assessments) ÷ all scored assessments × 100. For example, 30 positive and 10 negative out of 50 scored gives +40. Mixed and neutral remain in the denominator. This is not a satisfaction survey or market share.',
    ],
    [
      'Province map',
      'Net sentiment for the selected brand by province. Grey means no scored conversations there; check the conversation count before treating a colored province as representative.',
    ],
    [
      'Company comparison',
      'Counts distinct conversations naming each company. It does not assign co-mentioned products to that company, estimate market share or imply direct comparison. Product ownership needs a verified product master.',
    ],
    [
      'Campaigns',
      'A company-entered plan that can be compared with subsequent field conversations; it is not proof of campaign impact.',
    ],
  ],
  'Field Team': [
    [
      'Topics per conversation',
      'Approved entity mentions per recorded conversation; a breadth indicator, not a standalone quality score.',
    ],
    [
      'Location',
      'Where a conversation was recorded. The assigned field-team area and sub-districts come from onboarding; they are not inferred from that location.',
    ],
    [
      'Territory crop–issue link',
      'A reviewed link between a crop and an issue in one conversation. Each conversation counts once. The link records what was discussed, not proof of damage, an outbreak or a completed visit. Nearby mentions without a supported link stay out of the trend.',
    ],
    [
      'What was said',
      'Problem reported means the speaker described a current or past problem. Risk or prevention discussed means concern or preventive action without a confirmed problem. No current problem reported requires an explicit statement that the problem is not present. These meanings are reviewed against the original comment.',
    ],
    [
      'Matched sub-district',
      'An assigned sub-district with a recording whose place name uniquely matches in the same province, or whose place was matched by an administrator. Unmatched is not the same as unvisited.',
    ],
    [
      'Quality review',
      'Only conversations with an actual review are scored; an unreviewed agent must not be treated as poor quality. Compare people with similar recording coverage and review samples before coaching.',
    ],
  ],
  Analytics: [
    [
      'Marketing comparison',
      'Compare brand mentions and AI-scored sentiment over time. Company counts are distinct conversations naming a company, not market share. Campaign plans are company-entered targets, not measured campaign effects.',
    ],
    [
      'Sales comparison',
      'Compare one field team member with averages for their recorded province, assigned product team and country. Conversation count and topics per conversation measure contribution, not sales effectiveness.',
    ],
    [
      'Product and ingredient discussion',
      'Verified names mentioned in field conversations. A rise is a prompt for a supply-team check, not proof of orders, shortage or demand.',
    ],
    [
      'Issue discussion',
      'Verified pest, disease and plant-condition names by province. Recent and earlier rates count conversations naming the topic per 100 recorded conversations in each four-week window. The change is recent rate minus earlier rate, in percentage points.',
    ],
    [
      'Field staff and weeks',
      'Distinct field team members and recording weeks supporting a topic. These indicate breadth of evidence, not confidence that the issue affects all farms.',
    ],
  ],
  Reports: [
    [
      'Quick Report',
      'A pre-filled view of the selected period and location. Check every finding against its source evidence before sharing.',
    ],
    ['Build Report', 'Collect charts and tables from across the dashboard, then edit the commentary.'],
  ],
  Taxonomy: [],
};
const faqs = {
  'Market Brief': [
    [
      'Why is a brand marked Negative, Monitor or Positive?',
      'Negative means more negative than positive scored assessments with at least five conversations. Monitor means sentiment is mixed/weak or mentions have declined. Positive means net sentiment is at least +20 with sufficient volume and no declining-mention flag. Not enough means fewer than five scored conversations. Click the category to see the brands and the reason.',
    ],
    [
      'Why does Paddy in Subang need a check?',
      'The crop-location combination is being discussed more often. The line beneath it names the leading classified issue where one exists. If no specific issue is classified, open Field Intelligence and review the comments before assigning a cause.',
    ],
    [
      'Does a rise on the activity chart mean a market problem is growing?',
      'Not necessarily. It may simply mean more field conversations were recorded that week. Compare contributor count and the original comments before acting.',
    ],
  ],
  'Field Intelligence': [
    [
      'What does red mean in the location-and-crop table?',
      'It marks a combination for review under the current evidence rule. It does not establish an outbreak. Open the row, check issue type, conversation count and independent contributors, then read source comments.',
    ],
    [
      'Why are some provinces grey on the map?',
      'No matching recorded crop conversations are mapped to those provinces in the selected period. Grey does not mean no farmers have an issue there.',
    ],
    [
      'How should I use What is changing?',
      'First select a crop and location. The small trend shows mention history; the larger weekly chart compares total conversations with issue mentions. Read the full context beneath it to identify the specific issue and comments.',
    ],
    [
      'Why can I not filter weather or supply problems?',
      'The current approved issue taxonomy covers pests, diseases, weeds and plant condition. Weather and supply topics need consistent classification before they can be counted in the Issues view.',
    ],
  ],
  'Brand Intelligence': [
    [
      'What does a coloured brand/province cell mean?',
      'The number is net sentiment percentage: positive minus negative, divided by all scored conversations. The smaller number is supporting conversations. Select the cell to inspect comments; grey means insufficient scored evidence.',
    ],
    [
      'Why can a well-liked brand appear under Monitor?',
      'Monitor also captures declining mention frequency. A decline in mentions is not a decline in sentiment or sales. Check the movement chart and conversation coverage separately.',
    ],
    [
      'Are Syngenta and competitor counts market share?',
      'No. The chart counts conversations where each company was named. Green, red and grey portions describe available sentiment assessments; they do not show sales or establish product ownership.',
    ],
    [
      'How do I use Campaigns?',
      'Download the template, enter planned brand, location, dates, activity and targets, then upload it. Compare later conversations with the plan, but do not infer that the campaign caused a change without more evidence.',
    ],
  ],
  'Field Team': [
    [
      'Why is someone missing from the list?',
      'The page follows the selected date and geography filters. The person needs a recorded conversation in that scope. Reset filters or widen the period, then refresh if data has just been uploaded.',
    ],
    [
      'Can I rank people by conversation count?',
      'Use count for coverage, not quality. Compare similar territories and working periods, then inspect topics per conversation and actual reviewed examples.',
    ],
    [
      'Why is a quality score blank?',
      'No reviewed scorecard is available for that person. Blank does not mean zero or poor performance.',
    ],
    [
      'Why are some crop and issue pairs missing from Territory?',
      'The two names may appear in one conversation without being linked. The Territory trend includes only links reviewed against the recorded wording. Admins can inspect nearby mentions in Admin → Evidence review.',
    ],
    [
      'Does one crop–issue link mean there is an outbreak?',
      'No. Read the comment and compare independent conversations over time. One conditional statement or question is a reason to ask the field team, not a claim about incidence or severity.',
    ],
    [
      'Why is a crop–issue row amber rather than red?',
      'Amber means the conversation discussed risk or prevention, not confirmed damage. Open the row to read the source comment. Red means a problem was reported; green means the speaker explicitly reported no current problem.',
    ],
    [
      'Does no matched recording mean an assigned area was not visited?',
      'No. Recordings often use village or town names while assignments use sub-districts. Uncertain matches await admin review; no recording match is not a visit record.',
    ],
  ],
  Analytics: [
    [
      'When should I use Analytics instead of the main pages?',
      'Use Market Brief, Field Intelligence, Brand Intelligence and Field Team for a shared daily picture. Open Analytics when comparing brands, field-team peers, product discussion or issues across provinces for a specific decision.',
    ],
    [
      'Does a rising issue rate mean an outbreak?',
      'No. It means a greater share of recorded conversations named that issue. Check contributor count, recording coverage and the source comments before asking the field team to verify it.',
    ],
    [
      'Why is a product-team average missing?',
      'Only verified onboarding assignments are used. Without a confirmed product team, the dashboard does not infer one from conversation content.',
    ],
    [
      'Can procurement treat product mentions as demand?',
      'No. Mentions may be questions, complaints or comparisons. Combine this evidence with stock, orders and sales data when those systems become available.',
    ],
  ],
  Reports: [
    [
      'Will the preview match the PowerPoint?',
      'Each preview panel represents one exported slide and uses the same selected visual type and commentary. Check the downloaded file before sharing, especially if you edited a management note.',
    ],
    [
      'Can I include my own combination of views?',
      'Use Clip on a supported chart or table, open Build Report, arrange the selections and edit the accompanying text before downloading.',
    ],
  ],
  Taxonomy: [],
};

export default function Help() {
  const [page, setPage] = useState('Market Brief');
  const [taxonomy, setTaxonomy] = useState([]);
  useEffect(() => {
    axios
      .get('/api/decision/taxonomy')
      .then((r) => setTaxonomy(r.data?.types || []))
      .catch(() => setTaxonomy([]));
  }, []);
  return (
    <div className="help-page v2-page" style={{ padding: '24px', maxWidth: 1100, margin: '0 auto' }}>
      <div className="module-header">
        <div className="module-kicker">HELP</div>
        <h2>How to use the dashboard</h2>
        <p>Choose a page to see what its measures mean and where the evidence comes from.</p>
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', margin: '24px 0' }}>
        {Object.keys(pages).map((name) => (
          <button
            key={name}
            onClick={() => setPage(name)}
            className={page === name ? 'active' : ''}
            style={{
              padding: '9px 14px',
              border: '1px solid #d8d3c7',
              borderRadius: 20,
              background: page === name ? '#183f35' : 'white',
              color: page === name ? 'white' : '#183f35',
              cursor: 'pointer',
            }}
          >
            {name}
          </button>
        ))}
      </div>
      <section className="card" style={{ padding: 24 }}>
        <h3>{page}</h3>
        <p>
          {page === 'Market Brief'
            ? 'Start here for the management picture; open a subject in its dedicated page for the source comments.'
            : page === 'Field Intelligence'
              ? 'Narrow by location and crop, then inspect issues and the original conversations.'
              : page === 'Brand Intelligence'
                ? 'Compare brand perception, location and context before drawing a commercial conclusion.'
                : page === 'Field Team'
                  ? 'Compare contribution alongside recording coverage; do not rank quality from volume alone.'
                  : page === 'Analytics'
                    ? 'Choose a department and make one comparison at a time; the source conversations remain the evidence.'
                    : page === 'Taxonomy'
                      ? 'These are the entity types and named terms currently stored in this organization’s database. Counts are terms, not conversation mentions.'
                      : 'Select a period and location, then review and edit the report before distribution.'}
        </p>
        {page === 'Taxonomy' ? (
          <div className="help-taxonomy">
            {taxonomy.map((x) => (
              <details key={x.name}>
                <summary>
                  <b>{x.name}</b>
                  <span>{x.count} named terms</span>
                </summary>
                <p>
                  {x.examples.join(' · ')}
                  {x.count > x.examples.length ? ' · …' : ''}
                </p>
              </details>
            ))}
            {!taxonomy.length && <p>Taxonomy is unavailable right now.</p>}
          </div>
        ) : (
          <>
            <h4>Measures and definitions</h4>
            {pages[page].map(([name, definition]) => (
              <div key={name} style={{ borderTop: '1px solid #e6e0d5', padding: '14px 0' }}>
                <b>{name}</b>
                <p style={{ margin: '4px 0' }}>{definition}</p>
              </div>
            ))}
            <h4>Common questions</h4>
            {faqs[page].map(([question, answer]) => (
              <details key={question} style={{ borderTop: '1px solid #e6e0d5', padding: '13px 0' }}>
                <summary style={{ cursor: 'pointer', fontWeight: 700 }}>{question}</summary>
                <p style={{ margin: '9px 0 0', lineHeight: 1.5 }}>{answer}</p>
              </details>
            ))}
          </>
        )}
      </section>
      <p style={{ marginTop: 16, color: '#605c55' }}>
        Status colors: red = needs attention; amber = monitor; green = positive; grey = not enough evidence.
        They indicate conversation evidence, not target attainment. A status is a prompt to inspect comments,
        not a decision on its own.
      </p>
    </div>
  );
}
